<template>
  <div class="cover-container w-100 mx-auto" id="main_page" style="overflow: hidden;">
    <!-- A-Frame AR 場景（沿用 ARpage / MangoGamePage 的 8th Wall SLAM 對位方式）-->
    <a-scene v-if="isAFrameLoaded" ref="arScene" landing-page xrextras-loading xrextras-runtime-error
      renderer="colorManagement:true" xrweb="allowedDevices: any" embedded vr-mode-ui="enabled: false">

      <a-camera id="bugGameCamera" position="0 8 0" raycaster="objects: .cantap" cursor="fuse: false; rayOrigin: mouse;">
      </a-camera>

      <a-entity light="type: directional; intensity: 0.8; castShadow: false"
        xrextras-attach="target: camera; offset: 8 15 4" position="1 4.3 2.5">
      </a-entity>
      <a-light type="ambient" intensity="0.6"></a-light>

      <!-- 真實地面（不可見）：讓使用者直接點螢幕上看到的地面位置，用 A-Frame 內建 raycaster 求出真正的交點，
           比「鏡頭方向 × 固定距離」的土炮算法更貼合實際環境，對位後比較不會有飄移/懸空的感覺 -->
      <a-box id="bugGameGround" class="cantap" scale="1000 2 1000" position="0 -0.99 0"
        material="transparent: true; opacity: 0">
      </a-box>
    </a-scene>

    <!-- 對位提示：請使用者直接點選畫面上看到的苦楝樹底部位置（不再是固定準心） -->
    <div class="place-hint" v-if="isShowPlaceHint && !isTreePlaced">
      <div class="place-hint-badge">🎯</div>
      <div class="place-hint-text">{{ languageTexts[currentLanguage].placeHint }}</div>
    </div>

    <!-- 遊戲 HUD -->
    <div class="game-hud" v-if="isGameActive">
      <div class="game-hud-row">{{ languageTexts[currentLanguage].scoreLabel }}：{{ score }}</div>
      <div class="game-hud-row">{{ languageTexts[currentLanguage].progressLabel }}：{{ bugsHit }} / {{ winTarget }}</div>
    </div>

    <!-- 瞄準射線：拉彈弓時顯示虛線，指向放開後的實際發射方向（與拉的方向相反），尾端帶箭頭 -->
    <svg class="aim-line-svg" v-if="aimVisible">
      <defs>
        <marker id="aimArrowHead" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="14" markerHeight="14"
          markerUnits="userSpaceOnUse" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(255,255,255,0.9)" />
        </marker>
      </defs>
      <line :x1="slingAnchorX" :y1="slingAnchorY" :x2="aimLineEnd.x" :y2="aimLineEnd.y"
        stroke="rgba(255,255,255,0.85)" stroke-width="3" stroke-dasharray="8 8" marker-end="url(#aimArrowHead)" />
    </svg>

    <!-- 飛行中的彈丸（沿拋物線由 JS 逐幀更新座標）-->
    <div v-for="p in projectiles" :key="p.id" class="projectile" :style="{ left: p.x + 'px', top: p.y + 'px' }"></div>

    <!-- 彈弓（2D 覆蓋層）：橡皮筋連線 + 可拖曳的彈弓袋，放開後沿反方向發射 -->
    <div class="slingshot-zone" v-if="isGameActive">
      <div class="slingshot-band" :style="bandStyle"></div>
      <div class="slingshot-anchor" :style="{ left: slingAnchorX + 'px', top: slingAnchorY + 'px' }"></div>
      <div class="slingshot-pouch" :style="{ left: pouchX + 'px', top: pouchY + 'px' }"
        @touchstart.prevent="onSlingTouchStart" @mousedown.prevent="onSlingMouseDown">🔴</div>
    </div>

    <!-- 過關畫面 -->
    <div class="finish-page" v-if="isShowWinPage">
      <div class="finish-content">
        <h1>{{ languageTexts[currentLanguage].winTitle }}</h1>
        <p class="finish-score">{{ languageTexts[currentLanguage].winScoreLabel }}：{{ score }}</p>
        <div class="btn_area">
          <div class="btn_css" @click="restartGame">{{ languageTexts[currentLanguage].restartButton }}</div>
          <div class="btn_css" @click="onBackToMap">{{ languageTexts[currentLanguage].backMapButton }}</div>
        </div>
      </div>
    </div>

    <!-- 開發者模式：每隻蟲可以個別調整位置，即時反映在畫面上已生成的蟲 -->
    <div class="dev-toggle" v-if="devMode" @click="devPanelOpen = !devPanelOpen">⚙</div>
    <div class="dev-panel" v-if="devMode && devPanelOpen">
      <div class="dev-header">蟲的個別位置／大小調整</div>
      <div class="dev-label">選擇要調整的蟲</div>
      <div class="dev-row">
        <button v-for="(_, idx) in devBugOffsets" :key="idx" class="dev-btn dev-bug-select-btn"
          :class="{ 'dev-bug-select-active': idx === devSelectedBugIndex }" @click="devSelectedBugIndex = idx">
          {{ idx + 1 }}
        </button>
      </div>
      <div class="dev-label">左右 X（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjustBugOffset('x', -0.2)">−−</button>
        <button class="dev-btn" @click="devAdjustBugOffset('x', -0.05)">−</button>
        <span class="dev-value">{{ devBugOffsets[devSelectedBugIndex].x.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjustBugOffset('x', 0.05)">+</button>
        <button class="dev-btn" @click="devAdjustBugOffset('x', 0.2)">++</button>
      </div>
      <div class="dev-label">高度 Y（公尺，相對樹底）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjustBugOffset('y', -0.2, 0)">−−</button>
        <button class="dev-btn" @click="devAdjustBugOffset('y', -0.05, 0)">−</button>
        <span class="dev-value">{{ devBugOffsets[devSelectedBugIndex].y.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjustBugOffset('y', 0.05)">+</button>
        <button class="dev-btn" @click="devAdjustBugOffset('y', 0.2)">++</button>
      </div>
      <div class="dev-label">前後 Z（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjustBugOffset('z', -0.2)">−−</button>
        <button class="dev-btn" @click="devAdjustBugOffset('z', -0.05)">−</button>
        <span class="dev-value">{{ devBugOffsets[devSelectedBugIndex].z.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjustBugOffset('z', 0.05)">+</button>
        <button class="dev-btn" @click="devAdjustBugOffset('z', 0.2)">++</button>
      </div>
      <div class="dev-label">所有蟲共用的大小（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devBugSize', -0.03, 0.01)">−−</button>
        <button class="dev-btn" @click="devAdjust('devBugSize', -0.01, 0.01)">−</button>
        <span class="dev-value">{{ devBugSize.toFixed(3) }}</span>
        <button class="dev-btn" @click="devAdjust('devBugSize', 0.01)">+</button>
        <button class="dev-btn" @click="devAdjust('devBugSize', 0.03)">++</button>
      </div>
      <div class="dev-row">
        <button class="dev-btn dev-save-btn" :disabled="!isTreePlaced" @click="regenerateBugs">依目前數值重新生成蟲</button>
      </div>
    </div>

    <TopNavigation :showIndexBtn="isShowIndexBtn" :showMapBtn="isShowMapBtn" :showDataBtn="false"
      :currentLanguage="currentLanguage" @back-to-index="onBackToIndex" @back-to-map="onBackToMap" />
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import TopNavigation from '@/components/TopNavigation.vue'
import { withBase, getBasePath } from '@/utils/urlBase'

// 每隻蟲的預設位置（相對於錨點／樹底，公尺）：可在開發者面板逐一選擇、個別調整
const DEFAULT_BUG_OFFSETS = [
  { x: -2.7, y: 4.8, z: 0.1 },
  { x: 0.25, y: 5.5, z: -0.15 },
  { x: -2.15, y: 6.8, z: 0.15 },
  { x: 1, y: 7.9, z: 0 },
  { x: 3, y: 4.8, z: -0.2 },
]
// 所有蟲共用的大小（公尺），也可在開發者面板調整
const BUG_SIZE = 0.3
// 切換為 false 即關閉開發者調整面板
const DEV_MODE = true

// 彈弓拉開的最大視覺距離（像素）
const MAX_PULL_DISTANCE = 90
// 小於這個距離視為誤觸，不會發射
const MIN_PULL_DISTANCE = 20
// 命中判定的容許角度（度）：只要方向大致對到蟲就算命中，不考慮拉的力道
const HIT_ANGLE_TOLERANCE_DEG = 16
// 彈丸飛行動畫時長（毫秒）
const PROJECTILE_FLIGHT_MS = 600
// 拋物線弧頂高度上限（像素），實際弧高會依飛行距離等比縮放（見 computeArcHeight）
const PROJECTILE_ARC_HEIGHT_MAX = 200
// 拉弓瞄準時，白色拋物線預覽的固定長度（像素）；實際落點要放開才會依命中判定決定
const AIM_PREVIEW_DISTANCE = 260

export default {
  name: 'ChinaberryGamePage',
  components: {
    TopNavigation,
  },
  data() {
    return {
      isShowIndexBtn: false,
      isShowMapBtn: false,
      isAFrameLoaded: false,
      isShowPlaceHint: false,
      isTreePlaced: false,
      isGameActive: false,
      isShowWinPage: false,
      score: 0,
      bugsHit: 0,
      winTarget: 5,
      indexLink: window.location.protocol + '//' + window.location.host + getBasePath(),
      loadedScripts: [],
      // 開發者模式：每隻蟲可以個別調整位置，即時反映在畫面上已生成的蟲
      devMode: false,
      devPanelOpen: false,
      devSelectedBugIndex: 0,
      devBugOffsets: DEFAULT_BUG_OFFSETS.map(o => ({ ...o })),
      devBugSize: BUG_SIZE,
      // 彈弓狀態
      isPulling: false,
      isFiring: false, // 上一發彈丸尚未飛完（命中或飛出畫面）前，不能拉下一發
      slingAnchorX: 0,
      slingAnchorY: 0,
      pouchX: 0,
      pouchY: 0,
      aimVisible: false,
      aimAngleDeg: 0,
      projectiles: [],
      languageTexts: {
        zh: {
          placeHint: '請直接點選畫面上苦楝樹的底部位置',
          scoreLabel: '分數',
          progressLabel: '已擊中',
          winTitle: '恭喜過關！',
          winScoreLabel: '最終分數',
          restartButton: '再玩一次',
          backMapButton: '返回地圖',
        },
        en: {
          placeHint: 'Tap directly on the base of the Chinaberry tree shown on screen',
          scoreLabel: 'Score',
          progressLabel: 'Bugs Hit',
          winTitle: 'You Win!',
          winScoreLabel: 'Final Score',
          restartButton: 'Play Again',
          backMapButton: 'Back to Map',
        },
        bn: {
          placeHint: '画面に映っているセンダンの木の根元を直接タップしてください',
          scoreLabel: 'スコア',
          progressLabel: '命中数',
          winTitle: 'クリア！',
          winScoreLabel: '最終スコア',
          restartButton: 'もう一度遊ぶ',
          backMapButton: '地図に戻る',
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return Cookies.get('lang') || 'zh'
    },
    bandStyle() {
      if (!this.isPulling) return { display: 'none' }
      const dx = this.pouchX - this.slingAnchorX
      const dy = this.pouchY - this.slingAnchorY
      const dist = Math.hypot(dx, dy)
      const angleDeg = Math.atan2(dy, dx) * 180 / Math.PI
      return {
        left: this.slingAnchorX + 'px',
        top: this.slingAnchorY + 'px',
        width: dist + 'px',
        transform: `rotate(${angleDeg}deg)`,
      }
    },
    // 瞄準預覽線終點：沿瞄準方向（拉的反方向）固定取一段距離，畫成直線虛線＋箭頭，
    // 單純指示發射方向，不嘗試預測拋物線實際落點。
    aimLineEnd() {
      const angleRad = this.aimAngleDeg * Math.PI / 180
      return {
        x: this.slingAnchorX + Math.cos(angleRad) * AIM_PREVIEW_DISTANCE,
        y: this.slingAnchorY + Math.sin(angleRad) * AIM_PREVIEW_DISTANCE,
      }
    },
  },
  mounted() {
    if (DEV_MODE) {
      this.devMode = true
    }

    // iOS Safari bfcache 修復：頁面從快取還原時強制重新載入，避免 XR session 衝突
    this._onPageShow = (event) => {
      if (event.persisted) {
        window.location.reload()
      }
    }
    window.addEventListener('pageshow', this._onPageShow)

    this._activeBugs = []
    this._projectileSeq = 0

    this.load8thWallScripts().then(() => {
      this.isAFrameLoaded = true
      this.$nextTick(() => {
        const scene = this.$refs.arScene
        if (!scene) return
        if (scene.hasLoaded) {
          this.onSceneLoaded()
        } else {
          scene.addEventListener('loaded', () => this.onSceneLoaded(), { once: true })
        }
      })
    }).catch(error => {
      console.error('Failed to load 8thWall scripts:', error)
      alert('AR 場景載入失敗，請重新整理頁面')
    })
  },
  beforeUnmount() {
    if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow)
    }
    this.cleanupARScene()
  },
  methods: {
    onSceneLoaded() {
      this.removeVrModeUi()
      this.setupCameraReadyListener()
      this.setupGroundTapListener()
      setTimeout(() => {
        this.isShowIndexBtn = true
        this.isShowMapBtn = true
      }, 1000)
    },

    // 監聽地面點擊（沿用官方 aframe-world-effects-example 的 tap-place 寫法）：
    // camera 上的 raycaster + cursor 元件會在點擊 .cantap 物件時自動算出交點，
    // 從 event.detail.intersection.point 直接拿到真正對齊地面的世界座標
    setupGroundTapListener() {
      const ground = document.getElementById('bugGameGround')
      if (!ground) return
      this._onGroundTap = (event) => this.onGroundTap(event)
      ground.addEventListener('click', this._onGroundTap)
    },

    // 保險移除 A-Frame 自動插入的「進入 VR/AR」按鈕（vr-mode-ui="enabled:false" 未必能完全擋下）
    removeVrModeUi() {
      document.querySelectorAll('.a-enter-vr, .a-enter-ar, #vr-mode-ui-container').forEach(el => {
        if (el.parentNode) el.parentNode.removeChild(el)
      })
    },

    // 偵測相機取得權限並實際啟動後才顯示對位提示
    setupCameraReadyListener() {
      if (this._cameraModuleRegistered) return
      const vueInstance = this

      if (typeof XR !== 'undefined' && typeof XR.addCameraPipelineModule === 'function') {
        try {
          XR.addCameraPipelineModule({
            name: 'chinaberry-game-camera-ready',
            onStart: () => {
              vueInstance.isShowPlaceHint = true
            }
          })
          this._cameraModuleRegistered = true
          return
        } catch (e) {
          console.warn('[XR] camera pipeline module 設定失敗，採用延遲 fallback：', e)
        }
      }
      setTimeout(() => {
        vueInstance.isShowPlaceHint = true
      }, 2000)
    },

    // 點擊地面時（僅第一次點擊有效），直接使用 A-Frame cursor 算好的交點作為苦楝樹「樹底」錨點，
    // 使用者現在是直接點選畫面上看到的苦楝樹底部，而不是對準固定準心
    onGroundTap(event) {
      if (this.isTreePlaced) return
      const point = event.detail && event.detail.intersection ? event.detail.intersection.point : null
      if (!point) return
      this.placeTreeAt(point)
    },

    // 建立一個不可見的錨點，標記樹底位置，作為蟲的生成參考點（真實的苦楝樹由現場鏡頭畫面呈現，不疊加虛擬樹）
    // 注意：空的 a-entity 本身沒有幾何/材質可渲染，不需另外設 visible:false
    // （若設為 false，會連同底下掛載的蟲子節點一起被隱藏）
    placeTreeAt(point) {
      const scene = this.$refs.arScene
      if (!scene) return

      const anchor = document.createElement('a-entity')
      anchor.setAttribute('position', point)

      scene.appendChild(anchor)
      this._treeAnchorEl = anchor

      this.isTreePlaced = true
      this.isShowPlaceHint = false
      this.startGame()
    },

    startGame() {
      this.score = 0
      this.bugsHit = 0
      this.isShowWinPage = false
      this.isGameActive = true
      this.isPulling = false
      this.isFiring = false
      this.projectiles = []
      this.initSlingshotAnchor()
      this.spawnBugs()
    },

    restartGame() {
      this.startGame()
    },

    // ── 開發者模式：調整蟲的大小、以及個別調整每隻蟲的位置 ──────────────────
    devAdjust(field, delta, min = -Infinity) {
      this[field] = Math.max(min, parseFloat((this[field] + delta).toFixed(2)))
    },
    // 調整目前選到的那隻蟲的 x/y/z 座標，若該蟲已經生成在畫面上就立即同步移動
    devAdjustBugOffset(axis, delta, min = -Infinity) {
      const offset = this.devBugOffsets[this.devSelectedBugIndex]
      offset[axis] = Math.max(min, parseFloat((offset[axis] + delta).toFixed(2)))

      const bug = this._activeBugs && this._activeBugs[this.devSelectedBugIndex]
      if (bug) {
        bug.el.setAttribute('position', `${offset.x} ${offset.y} ${offset.z}`)
      }
    },
    // 依目前面板數值重新生成蟲（重置本局進度，方便反覆調整測試）
    regenerateBugs() {
      if (!this._treeAnchorEl) return
      this.score = 0
      this.bugsHit = 0
      this.spawnBugs()
    },

    initSlingshotAnchor() {
      this.slingAnchorX = window.innerWidth / 2
      this.slingAnchorY = window.innerHeight - 110
      this.pouchX = this.slingAnchorX
      this.pouchY = this.slingAnchorY
    },

    // ── 蟲的生成：一次生成 winTarget 隻，固定在樹上不動 ──
    // 每隻蟲的位置讀取 devBugOffsets[i]（可在開發者面板逐一選擇、個別調整），
    // 大小讀取 devBugSize（所有蟲共用），調整後按「重新生成蟲」套用，方便對照現場實際的苦楝樹微調。
    spawnBugs() {
      this.clearActiveBugs()
      if (!this._treeAnchorEl) return

      for (let i = 0; i < this.winTarget; i++) {
        const offset = this.devBugOffsets[i]

        const el = document.createElement('a-icosahedron')
        el.setAttribute('radius', this.devBugSize)
        el.setAttribute('color', '#2E2A1F')
        el.setAttribute('position', `${offset.x} ${offset.y} ${offset.z}`)
        this._treeAnchorEl.appendChild(el)

        this._activeBugs.push({ id: i, el, hit: false })
      }
    },
    clearActiveBugs() {
      if (!this._activeBugs) {
        this._activeBugs = []
        return
      }
      this._activeBugs.forEach(b => {
        if (b.el.parentNode) b.el.parentNode.removeChild(b.el)
      })
      this._activeBugs = []
    },

    // ── 彈弓拖曳（觸控／滑鼠共用）：按住彈弓袋往後拉，放開後沿反方向發射 ──
    onSlingTouchStart(e) {
      if (this.isFiring) return // 上一發還沒飛完，不能開始拉下一發
      const touch = e.touches[0]
      if (!touch) return
      this.isPulling = true
      this.updateSlingPull(touch.clientX, touch.clientY)

      this._onSlingMove = (moveEvent) => {
        const t = moveEvent.touches[0]
        if (!t) return
        this.updateSlingPull(t.clientX, t.clientY)
      }
      this._onSlingEnd = () => {
        this.releaseSling()
        window.removeEventListener('touchmove', this._onSlingMove)
        window.removeEventListener('touchend', this._onSlingEnd)
      }
      window.addEventListener('touchmove', this._onSlingMove)
      window.addEventListener('touchend', this._onSlingEnd)
    },
    onSlingMouseDown(e) {
      if (this.isFiring) return // 上一發還沒飛完，不能開始拉下一發
      this.isPulling = true
      this.updateSlingPull(e.clientX, e.clientY)

      this._onSlingMove = (moveEvent) => this.updateSlingPull(moveEvent.clientX, moveEvent.clientY)
      this._onSlingEnd = () => {
        this.releaseSling()
        window.removeEventListener('mousemove', this._onSlingMove)
        window.removeEventListener('mouseup', this._onSlingEnd)
      }
      window.addEventListener('mousemove', this._onSlingMove)
      window.addEventListener('mouseup', this._onSlingEnd)
    },
    updateSlingPull(clientX, clientY) {
      const dx = clientX - this.slingAnchorX
      const dy = clientY - this.slingAnchorY
      const dist = Math.min(Math.hypot(dx, dy), MAX_PULL_DISTANCE)
      const angle = Math.atan2(dy, dx)
      this.pouchX = this.slingAnchorX + Math.cos(angle) * dist
      this.pouchY = this.slingAnchorY + Math.sin(angle) * dist

      this.aimVisible = dist >= MIN_PULL_DISTANCE
      // 瞄準射線／發射方向 = 拉的反方向
      this.aimAngleDeg = angle * 180 / Math.PI + 180
    },
    releaseSling() {
      this.isPulling = false
      this.aimVisible = false

      const dx = this.pouchX - this.slingAnchorX
      const dy = this.pouchY - this.slingAnchorY
      const dist = Math.hypot(dx, dy)

      this.pouchX = this.slingAnchorX
      this.pouchY = this.slingAnchorY

      if (dist < MIN_PULL_DISTANCE) return // 拉太短視為取消，不發射

      this.fireSlingshot(-dx / dist, -dy / dist)
    },

    // ── 發射判定：只比對方向，在容許角度內取離瞄準線最近的蟲，不考慮拉的力道／距離 ──
    findBugAlongDirection(dirX, dirY) {
      const scene = this.$refs.arScene
      const cameraEl = document.getElementById('bugGameCamera')
      if (!scene || !cameraEl || !this._activeBugs.length) return null
      const cameraObj = cameraEl.getObject3D('camera')
      const canvas = scene.canvas
      if (!cameraObj || !canvas) return null

      const THREE = AFRAME.THREE
      const rect = canvas.getBoundingClientRect()
      const vec = new THREE.Vector3()
      const camWorldPos = new THREE.Vector3()
      cameraObj.getWorldPosition(camWorldPos)
      const camForward = new THREE.Vector3()
      cameraObj.getWorldDirection(camForward)

      const toleranceRad = HIT_ANGLE_TOLERANCE_DEG * Math.PI / 180
      let best = null
      let bestAngle = toleranceRad

      this._activeBugs.forEach(bug => {
        if (bug.hit) return
        bug.el.object3D.getWorldPosition(vec)

        // 只考慮鏡頭前方的蟲
        const toBug = vec.clone().sub(camWorldPos)
        if (toBug.dot(camForward) <= 0) return

        const projected = vec.clone().project(cameraObj)
        if (Math.abs(projected.x) > 1.3 || Math.abs(projected.y) > 1.3) return

        const screenX = rect.left + (projected.x + 1) / 2 * rect.width
        const screenY = rect.top + (1 - projected.y) / 2 * rect.height

        const toTargetX = screenX - this.slingAnchorX
        const toTargetY = screenY - this.slingAnchorY
        const targetDist = Math.hypot(toTargetX, toTargetY)
        if (targetDist < 1) return

        const dot = (toTargetX / targetDist) * dirX + (toTargetY / targetDist) * dirY
        const angle = Math.acos(Math.min(1, Math.max(-1, dot)))

        if (angle < bestAngle) {
          bestAngle = angle
          best = { bug, screenX, screenY }
        }
      })

      return best
    },
    fireSlingshot(dirX, dirY) {
      this.isFiring = true
      const target = this.findBugAlongDirection(dirX, dirY)

      let endX, endY
      if (target) {
        endX = target.screenX
        endY = target.screenY
      } else {
        const dist = Math.max(window.innerWidth, window.innerHeight) * 1.5
        endX = this.slingAnchorX + dirX * dist
        endY = this.slingAnchorY + dirY * dist
      }

      const id = ++this._projectileSeq
      const startX = this.slingAnchorX
      const startY = this.slingAnchorY
      this.projectiles.push({ id, x: startX, y: startY })

      this.animateProjectileArc(id, startX, startY, endX, endY, target)
    },
    // 拋物線弧頂高度：依飛行距離等比縮放，並限制上限。瞄準預覽線跟實際彈丸飛行共用同一個算法，
    // 確保拉弓時看到的曲線跟放開後球真正飛的弧線一致。
    computeArcHeight(dist) {
      return Math.min(PROJECTILE_ARC_HEIGHT_MAX, dist * 0.35)
    },
    // 逐幀計算拋物線位置：水平等速前進，垂直方向疊加一個往上隆起的正弦弧，
    // 讓彈丸看起來像拋物線飛出去，而不是直線移動到目標。
    animateProjectileArc(id, startX, startY, endX, endY, target) {
      const dist = Math.hypot(endX - startX, endY - startY)
      const arcHeight = this.computeArcHeight(dist)
      const startTime = performance.now()

      const step = (now) => {
        const t = Math.min(1, (now - startTime) / PROJECTILE_FLIGHT_MS)
        const p = this.projectiles.find(item => item.id === id)
        if (p) {
          p.x = startX + (endX - startX) * t
          p.y = startY + (endY - startY) * t - arcHeight * Math.sin(t * Math.PI)
        }

        if (t < 1) {
          requestAnimationFrame(step)
        } else {
          this.projectiles = this.projectiles.filter(item => item.id !== id)
          if (target) this.hitBug(target.bug)
          this.isFiring = false
        }
      }
      requestAnimationFrame(step)
    },
    hitBug(bug) {
      if (bug.hit) return
      bug.hit = true
      this._activeBugs = this._activeBugs.filter(b => b !== bug)

      bug.el.setAttribute('animation__hit', {
        property: 'scale',
        to: '0 0 0',
        dur: 200,
        easing: 'easeInBack'
      })
      setTimeout(() => {
        if (bug.el.parentNode) bug.el.parentNode.removeChild(bug.el)
      }, 220)

      this.score += 10
      this.bugsHit += 1

      if (this.bugsHit >= this.winTarget) {
        this.winGame()
      }
    },
    winGame() {
      this.isGameActive = false
      this.isShowWinPage = true
      this.clearActiveBugs()
      this.projectiles = []
    },

    // ── 場景清理 ────────────────────────────────────────────────────
    cleanupARScene() {
      this.clearActiveBugs()
      this.projectiles = []

      if (this._treeAnchorEl && this._treeAnchorEl.parentNode) {
        this._treeAnchorEl.parentNode.removeChild(this._treeAnchorEl)
      }
      this._treeAnchorEl = null

      const ground = document.getElementById('bugGameGround')
      if (ground && this._onGroundTap) {
        ground.removeEventListener('click', this._onGroundTap)
      }

      if (this._onSlingMove) {
        window.removeEventListener('touchmove', this._onSlingMove)
        window.removeEventListener('mousemove', this._onSlingMove)
      }
      if (this._onSlingEnd) {
        window.removeEventListener('touchend', this._onSlingEnd)
        window.removeEventListener('mouseup', this._onSlingEnd)
      }

      const scene = this.$refs.arScene
      if (scene && scene.hasLoaded) {
        try { scene.pause() } catch (e) { /* ignore */ }
      }
      if (typeof XR !== 'undefined') {
        try { XR.stop() } catch (e) { /* ignore */ }
      }
    },

    onBackToIndex() {
      window.location.href = this.indexLink
    },
    onBackToMap() {
      this.cleanupARScene()
      window.location.href = './mapPage'
    },

    // 載入 8thWall 所需的 scripts（與 ARpage.vue / MangoGamePage.vue 共用同一組本地檔案）
    load8thWallScripts() {
      return new Promise((resolve, reject) => {
        if (typeof window.AFRAME !== 'undefined') {
          resolve()
          return
        }

        const existingScripts = document.querySelectorAll('script[src*="8thwall"], script[src*="aframe"]')
        if (existingScripts.length > 0) {
          const checkAFrame = setInterval(() => {
            if (typeof window.AFRAME !== 'undefined') {
              clearInterval(checkAFrame)
              resolve()
            }
          }, 100)
          return
        }

        const aframeScript = document.createElement('script')
        aframeScript.src = withBase('/js/external/scripts/8frame-1.5.0.min.js')
        this.loadedScripts.push(aframeScript)

        aframeScript.onload = () => {
          const aframeExtraScript = document.createElement('script')
          aframeExtraScript.src = withBase('/js/external/xrextras/aframe-extras-6.1.1.min.js')
          this.loadedScripts.push(aframeExtraScript)

          aframeExtraScript.onload = () => {
            const xrExtrasScript = document.createElement('script')
            xrExtrasScript.src = withBase('/js/external/xrextras/xrextras.js')
            this.loadedScripts.push(xrExtrasScript)

            xrExtrasScript.onload = () => {
              const landingPageScript = document.createElement('script')
              landingPageScript.src = withBase('/js/external/landing-page/landing-page.js')
              this.loadedScripts.push(landingPageScript)

              landingPageScript.onload = () => {
                const eighthWallScript = document.createElement('script')
                eighthWallScript.src = withBase('/js/external/xr/xr.js')
                eighthWallScript.crossOrigin = 'anonymous'
                eighthWallScript.async = true
                eighthWallScript.setAttribute('data-preload-chunks', 'slam')
                this.loadedScripts.push(eighthWallScript)

                eighthWallScript.onload = () => resolve()
                eighthWallScript.onerror = () => reject(new Error('Failed to load 8thWall Web'))
                document.head.appendChild(eighthWallScript)
              }

              landingPageScript.onerror = () => reject(new Error('Failed to load Landing Page'))
              document.head.appendChild(landingPageScript)
            }

            xrExtrasScript.onerror = () => reject(new Error('Failed to load XR Extras'))
            document.head.appendChild(xrExtrasScript)
          }
          aframeExtraScript.onerror = () => reject(new Error('Failed to load XR Extras'))
          document.head.appendChild(aframeExtraScript)
        }

        aframeScript.onerror = () => reject(new Error('Failed to load A-Frame'))
        document.head.appendChild(aframeScript)
      })
    },
  }
}
</script>

<style scoped>
#main_page {
  height: 100svh;
  overflow: hidden;
  position: relative;
}

.place-hint {
  position: absolute;
  bottom: 32%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 500;
  pointer-events: none;
  text-align: center;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}

.place-hint-badge {
  font-size: 48px;
  animation: bob 1.2s ease-in-out infinite;
  display: inline-block;
}

@keyframes bob {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

.place-hint-text {
  margin-top: 8px;
  font-size: 15px;
  max-width: 80vw;
  margin-left: auto;
  margin-right: auto;
}

.game-hud {
  position: fixed;
  top: 90px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 500;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  border-radius: 16px;
  padding: 8px 20px;
  color: #fff;
  text-align: center;
}

.game-hud-row {
  font-size: 15px;
  font-family: 'MyFont', sans-serif;
}

.aim-line-svg {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 410;
  pointer-events: none;
  overflow: visible;
}

.projectile {
  position: fixed;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 35%, #ffb199, #c0392b 70%);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.35);
  z-index: 450;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.slingshot-zone {
  position: fixed;
  inset: 0;
  z-index: 400;
  pointer-events: none;
}

.slingshot-band {
  position: fixed;
  height: 4px;
  background: rgba(122, 74, 31, 0.8);
  transform-origin: left center;
  z-index: 420;
}

.slingshot-anchor {
  position: fixed;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.8);
  transform: translate(-50%, -50%);
  z-index: 420;
}

.slingshot-pouch {
  position: fixed;
  font-size: 34px;
  line-height: 1;
  transform: translate(-50%, -50%);
  pointer-events: auto;
  touch-action: none;
  cursor: grab;
  z-index: 430;
}

.finish-page {
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
}

.finish-content {
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  width: 80%;
  max-width: 360px;
}

.finish-content h1 {
  font-size: 22px;
  margin-bottom: 8px;
  color: #1a1a1a;
}

.finish-score {
  font-size: 16px;
  color: #477A17;
  margin-bottom: 20px;
}

.btn_area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn_css {
  background: linear-gradient(180deg, #668C42 0%, #477A17 100%);
  color: #fff;
  border-radius: 999px;
  padding: 12px;
  font-size: 15px;
  font-family: 'MyFont', sans-serif;
  letter-spacing: 1px;
  cursor: pointer;
}

/* ── 開發者模式 ── */
.dev-toggle {
  position: fixed;
  top: 80px;
  left: 10px;
  z-index: 10000;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  user-select: none;
}

.dev-panel {
  position: fixed;
  top: 120px;
  left: 10px;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.78);
  color: white;
  border-radius: 12px;
  padding: 12px;
  min-width: 230px;
  font-size: 13px;
  max-height: 50vh;
  overflow-y: auto;
}

.dev-header {
  font-weight: bold;
  font-size: 11px;
  color: #aaa;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
}

.dev-label {
  font-size: 10px;
  color: #888;
  margin-bottom: 3px;
}

.dev-scale-row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
}

.dev-value {
  flex: 1;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
}

.dev-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 14px;
  cursor: pointer;
  touch-action: manipulation;
  line-height: 1;
}

.dev-btn:active {
  background: rgba(255, 255, 255, 0.3);
}

.dev-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dev-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}

.dev-save-btn {
  background: rgba(67, 106, 178, 0.7);
  flex: 1;
}

.dev-bug-select-btn {
  flex: 1;
}

.dev-bug-select-active {
  background: rgba(67, 106, 178, 0.85);
  border-color: rgba(255, 255, 255, 0.6);
}
</style>

<!-- 非 scoped：A-Frame 的進入 VR/AR 按鈕是由其自身 JS 動態插入 DOM，不帶 Vue 的 scoped 屬性，
     scoped style 選不到，所以另外用全域樣式強制隱藏（雙重保險，JS 端也會嘗試移除該節點）-->
<style>
.a-enter-vr,
.a-enter-ar,
#vr-mode-ui-container {
  display: none !important;
}
</style>
